import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogOut, Search, Receipt, Mail, MapPin, Send, Phone, HelpCircle, Users, Star, Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { companyInfo } from '../mock/data';
import MobileMenu from '../components/MobileMenu';
import MobileBottomNav from '../components/MobileBottomNav';
import LanguageSelector from '../components/LanguageSelector';
import StarRating from '../components/StarRating';
import '../styles/HomePage.css';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PostPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const seo = useSEO('post', {
    title: 'Client Reviews & Testimonials | Registan Tourism Dubai — Rated 5 Stars',
    description: 'Read real client reviews about Registan Tourism. Rated 5/5 stars for Dubai visa services, UAE business setup, freelance visa, family visa, and guided tours.',
    keywords: 'registan tourism reviews, dubai visa service reviews, UAE visa agency testimonials, registan tours rating'
  });
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/reviews`);
      const data = await response.json();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    navigate('/');
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 5;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Registan Tourism",
    "description": "Professional visa services for UAE - Dubai Visa, Business Setup, Freelance Visa",
    "url": "https://registantours.com",
    "telephone": "+971-56-913-0707",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "UAE",
      "addressLocality": "Dubai"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": reviews.length,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviews.map(review => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": review.author },
      "datePublished": review.createdAt || review.date,
      "reviewBody": review.text,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5",
        "worstRating": "1"
      }
    }))
  };

  return (
    <div className="post-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)', color: '#fff' }}>
      <Helmet>
        <link rel="canonical" href="https://registantours.com/post" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/post" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {reviews.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData).replace(/</g, '\\u003c') }}
        />
      )}

      {/* Header - Same as Main Page */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/">
              <img src={currentLogo} alt="Registan" className="logo-img" />
            </Link>
          </div>
          <LanguageSelector />
          <button className="visa-status-btn" onClick={() => navigate('/')} data-testid="visa-status-btn">
            <Search size={16} />
            <span>{t('visaStatus') || 'Visa Status'}</span>
          </button>
          <button className="my-transactions-btn" onClick={() => navigate('/dashboard')} data-testid="my-transactions-btn">
            <Receipt size={16} />
            <span>{t('myTransactions') || 'My Transactions'}</span>
          </button>
        </div>
        <nav className="dark-nav">
          <div className="nav-dropdown">
            <Link to="/" className="dark-nav-link">{t('services')}</Link>
            <div className="dropdown-menu">
              <Link to="/" className="dropdown-item">Dubai Visa</Link>
              <Link to="/" className="dropdown-item">Visa Change</Link>
            </div>
          </div>
          <Link to="/start-business" className="dark-nav-link" data-testid="start-business-nav-btn">
            {t('startBusiness') || 'Start Business'}
          </Link>
        </nav>

        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login')}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp')}</button>
          </div>
        )}

        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/post" />

      <MobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        activeSection=""
        onNavigate={scrollToSection}
      />

      {/* Main Content - Using same review card style as homepage */}
      <div className="main-content" style={{ maxWidth: '960px', margin: '0 auto', padding: '40px 24px 60px' }}>
        <section id="reviews" className="content-section">
          <div className="reviews-header">
            <h2 className="section-title">{t('customerReviews')}</h2>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
              <p>Loading reviews...</p>
            </div>
          ) : (
            <div className="reviews-list">
              {reviews.map((review) => {
                const imageUrl = review.image_url 
                  ? (review.image_url.startsWith('http') ? review.image_url : `${BACKEND_URL}${review.image_url}`)
                  : (review.image ? (review.image.startsWith('http') ? review.image : `${BACKEND_URL}/api/uploads/${review.image}`) : null);
                
                return (
                  <div key={review.id} className="review-card" data-testid={`review-card-${review.id}`}>
                    <div className="review-header">
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={review.author} 
                          className="reviewer-photo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div className="reviewer-avatar" style={{ display: imageUrl ? 'none' : 'flex' }}>{review.author.charAt(0)}</div>
                      <div className="reviewer-info">
                        <h4>{review.author}</h4>
                        <StarRating rating={review.rating} readonly={true} />
                        {(review.instagram || review.tiktok) && (
                          <div className="reviewer-socials">
                            {review.instagram && (
                              <a href={`https://instagram.com/${review.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link instagram">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                              </a>
                            )}
                            {review.tiktok && (
                              <a href={`https://tiktok.com/@${review.tiktok.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link tiktok">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="review-date">{review.date}</span>
                    </div>
                    <p className="review-text">{review.text}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Footer - Same as Main Page */}
      <footer id="contact" className="site-footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-brand">
              <h2>Registan Tourism</h2>
              <p className="footer-tagline">{t('yourTrustedPartner')}</p>
            </div>
            <div className="footer-contact-info">
              <a href={`tel:${companyInfo.phone}`} className="footer-contact-item">
                <Phone size={18} />
                <span>{companyInfo.phone}</span>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="footer-contact-item">
                <Mail size={18} />
                <span>{companyInfo.email}</span>
              </a>
              <div className="footer-contact-item">
                <MapPin size={18} />
                <span>Dubai, United Arab Emirates</span>
              </div>
            </div>
            <a href="https://t.me/changeVISAoman" target="_blank" rel="noopener noreferrer" className="footer-telegram-btn">
              <Send size={18} />
              {t('joinTelegram')}
            </a>
            <button onClick={() => navigate('/faq')} className="footer-faq-btn" data-testid="faq-btn">
              <HelpCircle size={18} />
              {t('faq') || 'FAQ'}
            </button>
            <button onClick={() => navigate('/team')} className="footer-nav-btn" data-testid="footer-team-btn">
              <Users size={18} />
              {t('team') || 'Team'}
            </button>
            <button onClick={() => navigate('/')} className="footer-nav-btn" data-testid="footer-reviews-btn">
              <Star size={18} />
              {t('reviews') || 'Reviews'}
            </button>
          </div>
          <div className="footer-right">
            <div className="footer-good-to-know">
              <h3>{t('goodToKnow')}</h3>
              <div className="policy-badge">{t('bookingPolicy')}</div>
              <p>{t('bookingPolicyText')}</p>
            </div>
            <Link to="/privacy" className="footer-privacy-box" data-testid="footer-privacy-link">
              <Shield size={14} />
              <span>{t('privacyPolicy') || 'Privacy Policy'}</span>
            </Link>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Registan Tourism. {t('allRightsReserved')}.</p>
        </div>
      </footer>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default PostPage;
